import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Space,
  message,
  Tag,
  Typography,
  Popconfirm,
  Alert,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
} from '@ant-design/icons';
import api from '../../services/api';

const { Title } = Typography;
const { TextArea } = Input;

const ConfigManagement = ({ embedded = false }) => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showSecrets, setShowSecrets] = useState(false);
  const [form] = Form.useForm();

  const fetchConfigs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/api/config?show_secrets=${showSecrets}`);
      setConfigs(response.data.data || []);
    } catch (error) {
      message.error('Failed to load configurations');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [showSecrets]);

  useEffect(() => {
    fetchConfigs();
  }, [fetchConfigs]);

  const handleCreate = () => {
    setEditingConfig(null);
    form.resetFields();
    form.setFieldsValue({
      category: 'system',
      is_secret: false,
    });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingConfig(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (key) => {
    try {
      await api.delete(`/api/config/${key}`);
      message.success('Configuration deleted successfully');
      fetchConfigs();
    } catch (error) {
      message.error('Failed to delete configuration');
      console.error(error);
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingConfig) {
        await api.put(`/api/config/${editingConfig.key}`, values);
        message.success('Configuration updated successfully');
      } else {
        await api.post('/api/config', values);
        message.success('Configuration created successfully');
      }
      setModalVisible(false);
      fetchConfigs();
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to save configuration');
      console.error(error);
    }
  };

  const columns = [
    {
      title: 'Key',
      dataIndex: 'key',
      key: 'key',
      render: (text) => <code style={{ background: '#f0f0f0', padding: '2px 6px', borderRadius: 4 }}>{text}</code>,
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      ellipsis: true,
      render: (value, record) => {
        if (record.is_secret && !showSecrets) {
          return <Tag>***hidden***</Tag>;
        }
        return (
          <span style={{
            fontFamily: 'monospace',
            maxWidth: 300,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            display: 'inline-block',
          }}>
            {value || '-'}
          </span>
        );
      },
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category) => {
        const colors = {
          youtube: 'red',
          google: 'blue',
          scheduler: 'green',
          system: 'default',
        };
        return <Tag color={colors[category] || 'default'}>{category}</Tag>;
      },
      filters: [
        { text: 'YouTube', value: 'youtube' },
        { text: 'Google', value: 'google' },
        { text: 'Scheduler', value: 'scheduler' },
        { text: 'System', value: 'system' },
      ],
      onFilter: (value, record) => record.category === value,
    },
    {
      title: 'Secret',
      dataIndex: 'is_secret',
      key: 'is_secret',
      render: (isSecret) => isSecret ? <Tag color="red">Secret</Tag> : <Tag>Public</Tag>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Updated',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (date) => date ? new Date(date).toLocaleString() : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this configuration?"
            onConfirm={() => handleDelete(record.key)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger icon={<DeleteOutlined />} size="small">
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const content = (
    <>
      {!embedded && <Title level={3} style={{ margin: '0 0 20px 0' }}>Configuration Management</Title>}

      <div className="filters-row" style={{ justifyContent: 'space-between' }}>
        <Alert
          message="API Keys and Credentials"
          description="Store your YouTube API Key, Google Custom Search API Key, and other sensitive credentials here. Mark them as 'Secret' to hide values."
          type="info"
          showIcon
          style={{ flex: 1, marginRight: 12, marginBottom: 0 }}
        />
        <Space>
          <Button
            icon={showSecrets ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => setShowSecrets(!showSecrets)}
          >
            {showSecrets ? 'Hide' : 'Show'} Secrets
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreate}
          >
            Add Configuration
          </Button>
        </Space>
      </div>

        <Table
          columns={columns}
          dataSource={configs}
          loading={loading}
          rowKey="key"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} configurations`,
          }}
        />

        <Modal
          title={editingConfig ? 'Edit Configuration' : 'Create Configuration'}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          onOk={() => form.submit()}
          width={600}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
          >
            <Form.Item
              name="key"
              label="Key"
              rules={[{ required: true, message: 'Please enter configuration key' }]}
            >
              <Input
                placeholder="youtube_api_key"
                disabled={!!editingConfig}
              />
            </Form.Item>

            <Form.Item
              name="value"
              label="Value"
            >
              <TextArea
                rows={3}
                placeholder="Configuration value"
              />
            </Form.Item>

            <Form.Item
              name="category"
              label="Category"
              rules={[{ required: true, message: 'Please select category' }]}
            >
              <Select>
                <Select.Option value="youtube">YouTube</Select.Option>
                <Select.Option value="google">Google</Select.Option>
                <Select.Option value="scheduler">Scheduler</Select.Option>
                <Select.Option value="system">System</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="description"
              label="Description"
            >
              <TextArea
                rows={2}
                placeholder="Description of this configuration"
              />
            </Form.Item>

            <Form.Item
              name="is_secret"
              label="Mark as Secret"
              valuePropName="checked"
              help="Secret values will be hidden in the table view"
            >
              <Switch />
            </Form.Item>
          </Form>
        </Modal>
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <Card>{content}</Card>
  );
};

export default ConfigManagement;
